import { useState } from "react";
import { useLingui } from "@lingui/react/macro";

interface Props {
    initialInputs: InputData;
    onUpdate: (data: InputData) => void;
}

export type InputData = {
    quantity: string;
    pricePerHour: string;
    breakTimeMin: string;
    includeSetup: boolean;
    useHours: boolean;
};

/**
 * Displays a form for quick duration prediction.
 * Helps with dealing with numbers as strings to make forms happy.
 */
export default function QuickDurationPredictionForm({ initialInputs, onUpdate }: Props) {
    const { t } = useLingui();
    const [data, setData] = useState<InputData>(initialInputs);

    const handleUpdate = (key: keyof InputData, value: string | boolean) => {
        const newData = { ...data, [key]: value };
        setData(newData);
        onUpdate(newData);
    };

    return (
        <div className="d-flex flex-wrap gap-4 row-gap-2">
            <div className="card-content-main d-flex flex-wrap justify-content-start gap-1">
                <div className="card-row equal-columns">
                    <label htmlFor="input-quantity">{t`Quantity`}:</label>
                    <input
                        id="input-quantity"
                        className="form-control"
                        type="number"
                        min="1"
                        value={data.quantity}
                        onChange={e => handleUpdate("quantity", e.target.value)}
                    />
                </div>

                <div className="card-row equal-columns">
                    <label htmlFor="input-price-per-hour">{t`Machine Price/H`}:</label>
                    <input
                        id="input-price-per-hour"
                        className="form-control"
                        type="number"
                        min="0"
                        step="0.01"
                        value={data.pricePerHour}
                        onChange={e => handleUpdate("pricePerHour", e.target.value)}
                    />
                </div>

                <div className="card-row equal-columns">
                    <label htmlFor="input-break-time">{t`Break-time/shift (min)`}:</label>
                    <input
                        id="input-break-time"
                        className="form-control"
                        type="number"
                        min="0"
                        max="480"
                        value={data.breakTimeMin}
                        onChange={e => handleUpdate("breakTimeMin", e.target.value)}
                    />
                </div>
            </div>
            <div className="card-content-side d-flex flex-column gap-3">
                <label className="d-flex gap-2">
                    <input
                        type="checkbox"
                        checked={data.includeSetup}
                        onChange={e => handleUpdate("includeSetup", e.target.checked)}
                    />
                    {t`Include tool-setup`}
                </label>
                <div>
                    <div className="card-label mb-1">{t`Display Duration in`}:</div>
                    <div className="d-flex flex-column">
                        <label className="d-flex gap-2">
                            <input
                                type="radio"
                                name="durationUnit"
                                value="hours"
                                checked={data.useHours}
                                onChange={() => handleUpdate("useHours", true)}
                            />
                            {t`Hours`}
                        </label>
                        <label className="d-flex gap-2">
                            <input
                                type="radio"
                                name="durationUnit"
                                value="minutes"
                                checked={!data.useHours}
                                onChange={() => handleUpdate("useHours", false)}
                            />
                            {t`Minutes`}
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
}
